// Mock data for dashboard and modules

export interface CreditScore {
  bureau: 'Equifax' | 'Experian' | 'TransUnion';
  score: number;
  previousScore: number;
  color: string;
}

export interface DisputeItem {
  id: string;
  type: string;
  creditor: string;
  account: string;
  status: 'Disputable' | 'In Dispute' | 'Removed' | 'Verified';
  impact: number;
  selected: boolean;
}

export interface CreditHack {
  id: string;
  title: string;
  description: string;
  impact: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
}

export interface TaxDeduction {
  id: string;
  category: string;
  amount: number;
  confidence: number;
  strategy: string;
  claimed: boolean;
}

export interface FundingMatch {
  id: string;
  name: string;
  type: string;
  matchScore: number;
  amount: string;
  requirements: string[];
  logo?: string;
}

export interface GoalPhase {
  id: string;
  phase: number;
  title: string;
  tasks: { id: string; text: string; completed: boolean }[];
  locked: boolean;
  proTip: string;
}

export interface Client {
  id: string;
  name: string;
  email: string;
  score: number;
  plan: string;
  nextAction: string;
  monthlyPayment: number;
  joinDate: string;
}

// Credit Scores
export const creditScores: CreditScore[] = [
  { bureau: 'Equifax', score: 724, previousScore: 581, color: '#10b981' },
  { bureau: 'Experian', score: 715, previousScore: 592, color: '#3b82f6' },
  { bureau: 'TransUnion', score: 728, previousScore: 575, color: '#8b5cf6' },
];

// Dispute Items
export const disputeItems: DisputeItem[] = [
  { id: '1', type: 'Late Payment', creditor: 'Capital One', account: '****4521', status: 'Disputable', impact: 35, selected: false },
  { id: '2', type: 'Collection', creditor: 'Medical Associates', account: '****8832', status: 'Disputable', impact: 50, selected: false },
  { id: '3', type: 'Charge-off', creditor: 'Chase Bank', account: '****2341', status: 'In Dispute', impact: 80, selected: false },
  { id: '4', type: 'Inquiry', creditor: 'Auto Loan Inc', account: 'N/A', status: 'Disputable', impact: 5, selected: false },
  { id: '5', type: 'Late Payment', creditor: 'Discover', account: '****7623', status: 'Removed', impact: 25, selected: false },
  { id: '6', type: 'Collection', creditor: 'Utility Company', account: '****9912', status: 'Disputable', impact: 40, selected: false },
  { id: '7', type: 'Public Record', creditor: 'County Court', account: 'Civil Judgment', status: 'Verified', impact: 100, selected: false },
];

// Credit Hacks
export const creditHacks: CreditHack[] = [
  {
    id: '1',
    title: 'Pay Before Statement Closes',
    description: 'Pay your credit card balance before the statement closing date to report lower utilization to bureaus.',
    impact: '+15-30 points',
    difficulty: 'Easy',
  },
  {
    id: '2',
    title: 'Rapid Rescore',
    description: 'Request a rapid rescore through your mortgage lender after paying down balances for instant credit boost.',
    impact: '+20-50 points',
    difficulty: 'Medium',
  },
  {
    id: '3',
    title: 'Authorized User Trick',
    description: 'Get added as an authorized user on a family member\'s old, high-limit card with perfect payment history.',
    impact: '+30-60 points',
    difficulty: 'Easy',
  },
  {
    id: '4',
    title: '2% Utilization Secret',
    description: 'Keep utilization between 1-2% instead of 0% or 30%. This is the sweet spot for maximum score impact.',
    impact: '+10-25 points',
    difficulty: 'Easy',
  },
  {
    id: '5',
    title: 'Goodwill Letter',
    description: 'Write a goodwill letter to creditors asking them to remove late payments as a courtesy for loyal customers.',
    impact: '+20-40 points',
    difficulty: 'Medium',
  },
  {
    id: '6',
    title: 'Validation Request',
    description: 'Send debt validation letters to collection agencies forcing them to prove the debt or remove it.',
    impact: '+30-80 points',
    difficulty: 'Hard',
  },
];

// Tax Deductions
export const taxDeductions: TaxDeduction[] = [
  { id: '1', category: 'Home Office', amount: 4200, confidence: 95, strategy: 'Simplified method: 300 sq ft × $5/sq ft × 12 months', claimed: false },
  { id: '2', category: 'Vehicle Expenses', amount: 8400, confidence: 88, strategy: 'Standard mileage: 15,000 business miles × $0.655/mile (2023 rate)', claimed: false },
  { id: '3', category: 'Augusta Rule', amount: 6000, confidence: 92, strategy: 'Rent your home to your business for meetings: 14 days × $500/day', claimed: false },
  { id: '4', category: 'Health Insurance', amount: 12600, confidence: 98, strategy: 'Self-employed health insurance deduction for family coverage', claimed: false },
  { id: '5', category: 'Retirement Contributions', amount: 15000, confidence: 100, strategy: 'SEP IRA contribution: 20% of net self-employment income', claimed: false },
  { id: '6', category: 'Education & Training', amount: 2800, confidence: 75, strategy: 'Business-related courses, certifications, and professional development', claimed: false },
  { id: '7', category: 'S-Corp Savings', amount: 5200, confidence: 85, strategy: 'Payroll tax savings by electing S-Corp status and reasonable salary split', claimed: false },
];

// Funding Matches
export const fundingMatches: FundingMatch[] = [
  {
    id: '1',
    name: 'Chase Ink Business Cash',
    type: 'Business Credit Card',
    matchScore: 88,
    amount: '$25,000 - $50,000',
    requirements: ['680+ credit score', '2+ years in business', '$50k+ annual revenue'],
  },
  {
    id: '2',
    name: 'American Express Blue Business',
    type: 'Business Credit Card',
    matchScore: 65,
    amount: '$10,000 - $25,000',
    requirements: ['700+ credit score', '1+ year in business', 'Good payment history'],
  },
  {
    id: '3',
    name: 'SBA 7(a) Loan',
    type: 'Term Loan',
    matchScore: 52,
    amount: '$50,000 - $5,000,000',
    requirements: ['640+ credit score', 'Business plan', 'Collateral', '2+ years operating'],
  },
  {
    id: '4',
    name: 'Nav/Fundbox Line of Credit',
    type: 'Business Line of Credit',
    matchScore: 88,
    amount: '$10,000 - $100,000',
    requirements: ['600+ credit score', '$50k+ annual revenue', '6+ months in business'],
  },
  {
    id: '5',
    name: 'Federal Small Business Grant',
    type: 'Grant',
    matchScore: 35,
    amount: '$5,000 - $500,000',
    requirements: ['Specific industry', 'Detailed application', 'Minority/veteran owned (bonus)'],
  },
];

// Goal Phases - House
export const houseGoalPhases: GoalPhase[] = [
  {
    id: '1',
    phase: 1,
    title: 'Foundation (Current)',
    tasks: [
      { id: '1-1', text: 'Remove 2 medical collections', completed: true },
      { id: '1-2', text: 'Pay down credit cards to 10% utilization', completed: true },
      { id: '1-3', text: 'Get added as authorized user on parent\'s card', completed: false },
    ],
    locked: false,
    proTip: 'Focus on removing negative items first - they have the biggest impact on your score.',
  },
  {
    id: '2',
    phase: 2,
    title: 'Credit Building',
    tasks: [
      { id: '2-1', text: 'Open secured credit card ($500 deposit)', completed: false },
      { id: '2-2', text: 'Dispute late payment with Capital One', completed: false },
      { id: '2-3', text: 'Reach 680+ middle score', completed: false },
    ],
    locked: true,
    proTip: 'Lenders use your middle score (not average) for mortgage approval.',
  },
  {
    id: '3',
    phase: 3,
    title: 'Income Documentation',
    tasks: [
      { id: '3-1', text: 'Gather 2 years of tax returns', completed: false },
      { id: '3-2', text: 'Collect 2 months of bank statements', completed: false },
      { id: '3-3', text: 'Get pre-approval letter from lender', completed: false },
    ],
    locked: true,
    proTip: 'Self-employed? You\'ll need 2 years of tax returns showing consistent income.',
  },
  {
    id: '4',
    phase: 4,
    title: 'Down Payment & DTI',
    tasks: [
      { id: '4-1', text: 'Save 3.5% down payment (FHA) or 5% (Conventional)', completed: false },
      { id: '4-2', text: 'Lower debt-to-income ratio below 43%', completed: false },
      { id: '4-3', text: 'Get gift letter if using family funds', completed: false },
    ],
    locked: true,
    proTip: 'DTI = (Monthly debts ÷ Monthly income) × 100. Keep it under 43% for best rates.',
  },
  {
    id: '5',
    phase: 5,
    title: 'Mortgage Approval',
    tasks: [
      { id: '5-1', text: 'Submit full mortgage application', completed: false },
      { id: '5-2', text: 'Complete home inspection and appraisal', completed: false },
      { id: '5-3', text: 'Close on your new home!', completed: false },
    ],
    locked: true,
    proTip: 'Don\'t open new credit or make large purchases during underwriting!',
  },
];

// Clients (Admin Panel)
export const clients: Client[] = [
  { id: '1', name: 'Marcus Johnson', email: 'marcus.j@email.com', score: 724, plan: 'Elite', nextAction: 'Review tax deductions', monthlyPayment: 499, joinDate: '2025-09-15' },
  { id: '2', name: 'Sarah Chen', email: 'sarah.chen@email.com', score: 658, plan: 'Accelerator', nextAction: 'Dispute Capital One late payment', monthlyPayment: 149, joinDate: '2025-11-02' },
  { id: '3', name: 'David Martinez', email: 'd.martinez@email.com', score: 701, plan: 'Elite', nextAction: 'Apply for Chase Ink card', monthlyPayment: 499, joinDate: '2025-08-20' },
  { id: '4', name: 'Jennifer Williams', email: 'jen.williams@email.com', score: 689, plan: 'Accelerator', nextAction: 'Complete Phase 2 tasks', monthlyPayment: 149, joinDate: '2025-10-10' },
  { id: '5', name: 'Robert Thompson', email: 'rob.t@email.com', score: 742, plan: 'Funding Sprint', nextAction: 'Review SBA loan options', monthlyPayment: 1997, joinDate: '2025-07-05' },
  { id: '6', name: 'Amanda Foster', email: 'amanda.f@email.com', score: 615, plan: 'Starter', nextAction: 'Remove medical collection', monthlyPayment: 49, joinDate: '2025-12-01' },
];

// Chart data
export const creditScoreHistory = [
  { month: 'Jul', equifax: 581, experian: 592, transunion: 575 },
  { month: 'Aug', equifax: 598, experian: 605, transunion: 590 },
  { month: 'Sep', equifax: 625, experian: 630, transunion: 618 },
  { month: 'Oct', equifax: 658, experian: 665, transunion: 652 },
  { month: 'Nov', equifax: 692, experian: 688, transunion: 695 },
  { month: 'Dec', equifax: 724, experian: 715, transunion: 728 },
];

export const taxSavingsBreakdown = [
  { category: 'Vehicle', amount: 8400 },
  { category: 'Augusta Rule', amount: 6000 },
  { category: 'Health Ins', amount: 12600 },
  { category: 'Retirement', amount: 15000 },
  { category: 'S-Corp', amount: 5200 },
];
